import React from 'react';
import styled from 'styled-components';
import { KeyboardDetectionProvider, spacer24, screenMdMin, Type, screenSmMax, spacer16, screenXxsMax, ButtonPrimary, IconCheckAltFill, spacer8, spacer32, screenMdMax, spacer48, cssColorValue, spacer40, createBasicColorSet, desktopAlto, } from '@spotify-internal/encore-web';
import { DELAYS, fadeInAnimation, Grid, withAnimationContext, IconNumberFill, TWO_COLUMN_MAX_WIDTH, MD_GRID_MAX_WIDTH, } from '../../ui';
import { fixHref, formatText } from '../../ui/utils';
import { ColorSetWrapper } from '../../ui/Colors/Colors';
import { Media } from '../../ui/Components/Media';
const ChecklistModuleWrapper = styled(Grid) `
  padding-top: ${spacer48};
  padding-bottom: ${({ hasCarousel }) => (hasCarousel ? spacer24 : spacer40)};
  height: auto;
  @media (max-width: ${MD_GRID_MAX_WIDTH}) {
    padding-top: ${spacer40};
  }
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    padding-bottom: ${spacer40};
  }
  @media (max-width: ${screenXxsMax}) {
    padding-top: ${spacer24};
    padding-bottom: ${spacer48};
  }
`;
const InnerText = styled.div `
  height: auto;
  align-self: center;

  @media (max-width: ${screenXxsMax}) {
    width: 100%;
    justify-content: center;
  }
`;
const EyeBrowWrapper = withAnimationContext(styled.div `
  ${fadeInAnimation(DELAYS.FIRST)};
`);
const EyebrowText = styled(Type.p).attrs({
    variant: 'ballad',
}) `
  padding-bottom: ${spacer8};
`;
const EyeBrowNumber = styled.div `
  padding-bottom: ${spacer16};
`;
const Headline = withAnimationContext(styled(Type.h1).attrs({
    paddingBottom: '0.67em',
    variant: 'altoBrio',
    weight: 'bold',
}) `
    ${fadeInAnimation(DELAYS.SECOND)};
    padding-bottom: 0px;
    max-width: 684px;
    line-height: normal;

    @media (max-width: ${screenMdMax}) {
      font-size: ${desktopAlto.fontSize};
    }

    @media (min-width: ${screenMdMin}) {
      text-align: left;
    }
  `);
const Description = withAnimationContext(styled(Type.p) `
    ${fadeInAnimation(DELAYS.THIRD)};
    // override markdown text links formatting
    a {
      color: ${cssColorValue('textBase')};
      text-decoration: underline;
      &:hover:not(:focus):not(:disabled) {
        color: ${cssColorValue('textBrightAccent')};
      }
    }
    padding-top: ${spacer16};
    padding-bottom: ${spacer16};
  `);
const ChecklistIconWrapper = styled.div `
  display: flex;
  height: 1.3em;
  align-items: center;
  color: ${({ color }) => color};
  fill: ${({ color }) => color};
`;
const ChecklistWrapper = withAnimationContext(styled.div `
  ${fadeInAnimation(DELAYS.FOURTH)};
`);
const ChecklistItem = styled.li `
  display: flex;
  padding-bottom: ${({ paddingBottom }) => paddingBottom};
  // override markdown text links formatting
  a {
    color: ${cssColorValue('textBase')};
    text-decoration: underline;
    &:hover:not(:focus):not(:disabled) {
      color: ${cssColorValue('textBrightAccent')};
    }
  }
`;
const CTAContainer = withAnimationContext(styled.div `
    ${fadeInAnimation(DELAYS.FIFTH)};
    display: flex;
    padding-top: ${spacer32};

    @media (max-width: ${screenXxsMax}) {
      width: 100%;
      justify-content: center;
    }
  `);
const MediaBox = styled.div `
  grid-column: ${({ layout }) => (layout === 'left' ? '1 / 6' : '8 / 13')};
  order: ${({ layout }) => (layout === 'left' ? '1' : '2')};
  align-self: center;
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    grid-column: 1 / -1;
    order: 1;
  }
  @media (max-width: ${screenSmMax}) {
    margin-bottom: ${spacer24};
  }
`;
const TextBox = styled.div `
  display: flex;
  justify-content: flex-start;
  grid-column: ${({ layout }) => (layout === 'left' ? '7 / 12' : '2 / 7')};
  order: ${({ layout }) => (layout === 'left' ? '2' : '1')};

  @media (max-width: ${screenMdMax}) {
    grid-column: ${({ layout }) => (layout === 'left' ? '7 / 13' : '1 / 7')};
  }
  @media (max-width: ${TWO_COLUMN_MAX_WIDTH}) {
    grid-column: 1 / -1;
    order: 2;
  }
`;
export const ChecklistModule = React.forwardRef(({ eyebrowNumber, eyebrowText, headline, description, checklistItems, isNumberedChecklist, image, media, ctaLink, ctaText, ctaNewTab, colors, layout = 'left', btnComponent, }, ref) => {
    const formattedTextColor = (colors === null || colors === void 0 ? void 0 : colors.textColor) || ((colors === null || colors === void 0 ? void 0 : colors.theme) === 'dark' ? '#FFFFFF' : '#000000');
    const formattedColors = {
        backgroundColor: (colors === null || colors === void 0 ? void 0 : colors.backgroundColor) ||
            ((colors === null || colors === void 0 ? void 0 : colors.theme) === 'dark' ? '#000000' : '#FFFFFF'),
        textColor: formattedTextColor,
        accentColor: (colors === null || colors === void 0 ? void 0 : colors.accentColor) || (colors === null || colors === void 0 ? void 0 : colors.iconColor),
    };
    return (React.createElement(KeyboardDetectionProvider, null,
        React.createElement(ColorSetWrapper, { colors: formattedColors },
            React.createElement(ChecklistModuleWrapper, { hasCarousel: (media === null || media === void 0 ? void 0 : media.contentType) === 'compactCarousel' },
                React.createElement(MediaBox, { layout: layout },
                    React.createElement(Media, { media: media, image: image })),
                React.createElement(TextBox, { layout: layout, ref: ref },
                    React.createElement(InnerText, null,
                        (eyebrowNumber || eyebrowText) && (React.createElement(EyeBrowWrapper, null, eyebrowText ? (React.createElement(EyebrowText, { "data-testid": "checklist-module-eyebrow", dangerouslySetInnerHTML: {
                                __html: formatText(eyebrowText.toUpperCase()),
                            } })) : (React.createElement(EyeBrowNumber, null,
                            React.createElement(IconNumberFill, { number: eyebrowNumber, size: 24, fillColor: cssColorValue('textBase') }))))),
                        headline && (React.createElement(Headline, { "data-testid": "checklist-module-headline", dangerouslySetInnerHTML: { __html: formatText(headline) } })),
                        description && (React.createElement(Description, { "data-testid": "checklist-module-description", variant: "ballad", dangerouslySetInnerHTML: {
                                __html: formatText(description),
                            } })),
                        checklistItems && (React.createElement(ChecklistWrapper, null, checklistItems.map((item, idx) => {
                            const itemNumber = idx + 1;
                            return (React.createElement(React.Fragment, null,
                                React.createElement(ChecklistItem, { key: item, paddingBottom: idx < checklistItems.length - 1
                                        ? spacer16
                                        : undefined },
                                    React.createElement(ChecklistIconWrapper, { color: formattedColors.accentColor ||
                                            cssColorValue('textBase') }, isNumberedChecklist ? (React.createElement(IconNumberFill, { number: itemNumber, size: 16 })) : (React.createElement(IconCheckAltFill, { iconSize: 16 }))),
                                    React.createElement(Type, { style: { paddingLeft: spacer8 }, variant: "ballad", dangerouslySetInnerHTML: {
                                            __html: formatText(item),
                                        } }))));
                        }))),
                        ctaText && ctaLink && (React.createElement(CTAContainer, null,
                            React.createElement(ButtonPrimary, { href: fixHref(ctaLink) || '', target: ctaNewTab ? '_blank' : '_self', UNSAFE_colorSet: createBasicColorSet(formattedColors.textColor, formattedColors.backgroundColor), component: btnComponent }, ctaText)))))))));
});
//# sourceMappingURL=index.js.map